import {
  statesFromStateDescription,
  getTransitionsToStateFn,
  getTransitions,
  txLastTransition,
  hasPassedTransition,
} from '../transaction';

/**
 * Transitions
 *
 * These strings must sync with values defined in Flex API,
 * since transaction objects given by API contain info about last transitions.
 * All the actions in API side happen in transitions,
 * so we need to understand what those strings mean.
 */

// When a customer makes a booking to a listing, a transaction is
// created with the initial request-payment transition.
// At this transition a PaymentIntent is created by Marketplace API.
// After this transition, the actual payment must be made on client-side directly to Stripe.
export const TRANSITION_REQUEST_PAYMENT = 'transition/request-payment';

// A customer can also initiate a transaction with an enquiry, and
// then transition that with a request.
export const TRANSITION_ENQUIRE = 'transition/enquire';
export const TRANSITION_REQUEST_PAYMENT_AFTER_ENQUIRY = 'transition/request-payment-after-enquiry';

//Main rental
export const TRANSITION_REQUEST_OFFER = 'transition/request-offer';
export const TRANSITION_RE_OFFER = 'transition/re-offer';
export const TRANSITION_DECLINE_OFFER = 'transition/decline-offer';
export const TRANSITION_ACCEPT_NEGOTIATE_OFFER = 'transition/accept-negotiate-offer';
export const TRANSITION_DECLINE = 'transition/decline';
export const TRANSITION_EXPIRE = 'transition/expire';
export const TRANSITION_ACCEPT = 'transition/accept';
export const TRANSITION_RESCHEDULE = 'transition/reschedule';
export const TRANSITION_ACCEPT_RESCHEDULE = 'transition/accept-reschedule';
export const TRANSITION_CANCEL_RESCHEDULE = 'transition/cancel-reschedule';
export const TRANSITION_EXPIRE_RESCHEDULE = 'transition/expire-reschedule';
export const TRANSITION_CONFIRM_RESCHEDULE_ACCEPTANCE = 'transition/confirm-reschedule-acceptance';
export const TRANSITION_EXPIRE_RESCHEDULE_ACCEPTANCE = 'transition/expire-reschedule-acceptance';
export const TRANSITION_MARK_PENDING_PAYMENT = 'transition/mark-pending-payment';
export const TRANSITION_EXPIRE_PENDING_PAYMENT = 'transition/expire-pending-payment';
export const TRANSITION_CREATE_PAYMENT = 'transition/create-payment';
export const TRANSITION_AUTO_RECREATE_PAYMENT = 'transition/auto-recreate-payment';
export const TRANSITION_AUTO_CAPTURE_PAYMENT = 'transition/auto-capture-payment';
export const TRANSITION_INITIATE_PAYMENT = 'transition/initiate-payment';
export const TRANSITION_CONFIRM_PAYMENT = 'transition/confirm-payment';
export const TRANSITION_TRANSFERRED = 'transition/transferred';
export const TRANSITION_DISPUTE = 'transition/dispute';
export const TRANSITION_MARK_RETURN_ITEM = 'transition/mark-return-item';
export const TRANSITION_EXPIRE_RETURN_ITEM = 'transition/expire-return-item';
export const TRANSITION_REQUEST_EXTEND_BOOKING = 'transition/request-extend-booking';
export const TRANSITION_MARK_RETURN_ITEM_AFTER_REQUEST_EXTEND_BOOKING =
  'transition/mark-return-item-after-request-extend-booking';
export const TRANSITION_EXPIRE_RETURN_ITEM_AFTER_REQUEST_EXTEND_BOOKING =
  'transition/expire-return-item-after-request-extend-booking';
export const TRANSITION_OFFER_EXTEND_BOOKING_DIRECTLY = 'transition/offer-extend-booking-directly';
export const TRANSITION_OFFER_EXTEND_BOOKING = 'transition/offer-extend-booking';
export const TRANSITION_DECLINE_EXTEND_BOOKING = 'transition/decline-extend-booking';
export const TRANSITION_EXPIRE_RETURN_ITEM_AFTER_OFFERED_EXTEND_BOOKING =
  'transition/expire-return-item-after-offered-extend-booking';
export const TRANSITION_ACCEPT_EXTEND_BOOKING = 'transition/accept-extend-booking';
export const TRANSITION_COMPLETE_BOOKING = 'transition/complete-booking';
export const TRANSITION_START_REVIEW = 'transition/start-review';
export const TRANSITION_MARK_RETURN_ITEM_AFTER_OFFER_EXTEND_BOOKING =
  'transition/mark-return-item-after-offer-extend-booking';
export const TRANSITION_HOLD_CAPTURE_PAYMENT = 'transition/hold-capture-payment';
export const TRANSITION_SAVE_EXTEND_BOOKING = 'transition/save-extend-booking';
export const TRANSITION_DISPUTE_AFTER_REQUEST_EXTEND_BOOKING =
  'transition/dispute-after-request-extend-booking';
export const TRANSITION_REMIND_REVIEW = 'transition/remind-review';
export const TRANSITION_REVIEW_1_BY_PROVIDER_AFTER_REMINDER =
  'transition/review-1-by-provider-after-reminder';
export const TRANSITION_REMIND_CUSTOMER_REVIEW = 'transition/remind-customer-review';
export const TRANSITION_REVIEW_2_BY_PROVIDER_AFTER_REMINDER =
  'transition/review-2-by-provider-after-reminder';
export const TRANSITION_REVIEW_1_BY_CUSTOMER_AFTER_REMINDER =
  'transition/review-1-by-customer-after-reminder';
export const TRANSITION_REMIND_PROVIDER_REVIEW = 'transition/remind-provider-review';
export const TRANSITION_REVIEW_2_BY_CUSTOMER_AFTER_REMINDER =
  'transition/review-2-by-customer-after-reminder';
export const TRANSITION_EXPIRE_REVIEW_PERIOD_AFTER_REMINDER =
  'transition/expire-review-period-after-reminder';
export const TRANSITION_EXPIRE_PROVIDER_REVIEW_PERIOD_AFTER_REMINDER =
  'transition/expire-provider-review-period-after-reminder';
export const TRANSITION_EXPIRE_CUSTOMER_REVIEW_PERIOD_AFTER_REMINDER =
  'transition/expire-customer-review-period-after-reminder';

// If the payment is not confirmed in the time limit set in transaction process (by default 15min)
// the transaction will expire automatically.
export const TRANSITION_EXPIRE_PAYMENT = 'transition/expire-payment';

// Admin can also cancel the transition.
export const TRANSITION_CANCEL = 'transition/cancel';

// The backend will mark the transaction completed.
export const TRANSITION_COMPLETE = 'transition/complete';

// Reviews are given through transaction transitions. Review 1 can be
// by provider or customer, and review 2 will be the other party of
// the transaction.
export const TRANSITION_REVIEW_1_BY_PROVIDER = 'transition/review-1-by-provider';
export const TRANSITION_REVIEW_2_BY_PROVIDER = 'transition/review-2-by-provider';
export const TRANSITION_REVIEW_1_BY_CUSTOMER = 'transition/review-1-by-customer';
export const TRANSITION_REVIEW_2_BY_CUSTOMER = 'transition/review-2-by-customer';
export const TRANSITION_EXPIRE_CUSTOMER_REVIEW_PERIOD = 'transition/expire-customer-review-period';
export const TRANSITION_EXPIRE_PROVIDER_REVIEW_PERIOD = 'transition/expire-provider-review-period';
export const TRANSITION_EXPIRE_REVIEW_PERIOD = 'transition/expire-review-period';
export const TRANSITION_DISPUTE_AFTER_OFFERED_EXTEND_BOOKING =
  'transition/dispute-after-offered-extend-booking';
export const TRANSITION_RESOLVED = 'transition/resolved';

// Admin transition
export const TRANSITION_ADMIN_DECLINE = 'transition/admin-decline';
export const TRANSITION_ADMIN_ACCEPT = 'transition/admin-accept';
export const TRANSITION_CANCEL_BOOKING = 'transition/admin-cancel-booking';
export const TRANSITION_ADMIN_CREATE_PAYMENT = 'transition/admin-create-payment';
export const TRANSITION_CANCEL_CREATED_PAYMENT = 'transition/admin-cancel-created-payment';
export const TRANSITION_CANCEL_PRE_CREATE_PAYMENT = 'transition/admin-cancel-pre-create-payment';
export const TRANSITION_CANCEL_PRE_AUTO_CAPTURE_PAYMENT =
  'transition/admin-cancel-pre-auto-capture-payment';
export const TRANSITION_ADMIN_MARK_RETURN_ITEM = 'transition/admin-mark-return-item';
export const TRANSITION_ADMIN_MARK_RETURN_ITEM_AFTER_REQUEST_EXTEND_BOOKING =
  'transition/admin-mark-return-item-after-request-extend-booking';
export const TRANSITION_ADMIN_MARK_RETURN_ITEM_AFTER_OFFER_EXTEND_BOOKING =
  'transition/admin-mark-return-item-after-offer-extend-booking';

//More action
export const ACTION_CUSTOMER_EXTEND_OFFER = 'custom-action/action-customer-extend-offer';
export const ACTION_MANUAL_RETRY_PAYMENT = 'custom-action/action-manual-retry-payment';
export const ACTION_RETRY_WITH_CURRENT_CARD = 'custom-action/action-retry-with-current-card';

const STATE_INITIAL = 'initial';
const STATE_ENQUIRY = 'enquiry';
const STATE_PENDING_PAYMENT = 'pending-payment';
const STATE_PAYMENT_EXPIRED = 'payment-expired';
const STATE_DECLINED = 'declined';
const STATE_RESCHEDULED = 'rescheduled';
const STATE_RESCHEDULE_ACCEPTED = 'reschedule-accepted';
const STATE_ACCEPTED = 'accepted';
const STATE_DELIVERED = 'delivered';
const STATE_REVIEWED = 'reviewed';
const STATE_REVIEWED_BY_CUSTOMER = 'reviewed-by-customer';
const STATE_REVIEWED_BY_PROVIDER = 'reviewed-by-provider';
const STATE_REQUESTED = 'requested';
const STATE_OFFER_REQUESTED = 'offer-requested';
const STATE_CANCELLED = 'cancelled';
const STATE_PRE_CREATE_PAYMENT = 'pre-create-payment';
const STATE_CREATED_PAYMENT = 'created-payment';
const STATE_CONFIRMED_PAYMENT = 'confirmed-payment';
const STATE_RETURNED_ITEM = 'returned-item';
const STATE_PRE_BOOKING_EXTEND = 'pre-booking-extend';
const STATE_OFFERED_EXTEND_BOOKING = 'offered-extend-booking';
const STATE_EXTENDED_BOOKING = 'extended-booking';
const STATE_REVIEW_STARTING = 'review-starting';
const STATE_PRE_AUTO_CAPTURE_PAYMENT = 'pre-auto-capture-payment';
const STATE_DISPUTED = 'disputed';
const STATE_REMINDED_REVIEW = 'reminded-review';
const STATE_REMINDED_CUSTOMER_REVIEW = 'reminded-customer-review';
const STATE_REMINDED_PROVIDER_REVIEW = 'reminded-provider-review';

export const stateDescription = {
  // id is defined only to support Xstate format.
  // However if you have multiple transaction processes defined,
  // it is best to keep them in sync with transaction process aliases.
  id: 'flex-default-process/release-1',

  // This 'initial' state is a starting point for new transaction
  initial: STATE_INITIAL,

  // States
  states: {
    [STATE_INITIAL]: {
      on: {
        [TRANSITION_ENQUIRE]: STATE_ENQUIRY,
        [TRANSITION_REQUEST_PAYMENT]: STATE_REQUESTED,
      },
    },
    [STATE_PAYMENT_EXPIRED]: {},
    [STATE_ENQUIRY]: {
      on: {
        [TRANSITION_REQUEST_PAYMENT_AFTER_ENQUIRY]: STATE_REQUESTED,
        [TRANSITION_REQUEST_OFFER]: STATE_OFFER_REQUESTED,
      },
    },
    [STATE_REQUESTED]: {
      on: {
        [TRANSITION_DECLINE]: STATE_DECLINED,
        [TRANSITION_EXPIRE]: STATE_DECLINED,
        [TRANSITION_ACCEPT]: STATE_ACCEPTED,
        [TRANSITION_RESCHEDULE]: STATE_RESCHEDULED,
        [TRANSITION_ADMIN_ACCEPT]: STATE_ACCEPTED,
        [TRANSITION_ADMIN_DECLINE]: STATE_DECLINED,
        [TRANSITION_ADMIN_ACCEPT]: STATE_ACCEPTED,
      },
    },
    [STATE_OFFER_REQUESTED]: {
      on: {
        [TRANSITION_RE_OFFER]: STATE_OFFER_REQUESTED,
        [TRANSITION_DECLINE_OFFER]: STATE_OFFER_REQUESTED,
        [TRANSITION_ACCEPT_NEGOTIATE_OFFER]: STATE_REQUESTED,
      },
    },
    [STATE_RESCHEDULED]: {
      on: {
        [TRANSITION_ACCEPT_RESCHEDULE]: STATE_RESCHEDULE_ACCEPTED,
        [TRANSITION_CANCEL_RESCHEDULE]: STATE_DECLINED,
        [TRANSITION_EXPIRE_RESCHEDULE]: STATE_DECLINED,
      },
    },
    [STATE_RESCHEDULE_ACCEPTED]: {
      on: {
        [TRANSITION_CONFIRM_RESCHEDULE_ACCEPTANCE]: STATE_ACCEPTED,
        [TRANSITION_EXPIRE_RESCHEDULE_ACCEPTANCE]: STATE_DECLINED,
      },
    },
    [STATE_DECLINED]: {},
    [STATE_ACCEPTED]: {
      on: {
        [TRANSITION_CANCEL_BOOKING]: STATE_CANCELLED,
        [TRANSITION_MARK_PENDING_PAYMENT]: STATE_PRE_CREATE_PAYMENT,
      },
    },
    [STATE_CANCELLED]: {},
    [STATE_PRE_CREATE_PAYMENT]: {
      on: {
        [TRANSITION_EXPIRE_PENDING_PAYMENT]: STATE_CANCELLED,
        [TRANSITION_CREATE_PAYMENT]: STATE_CREATED_PAYMENT,
        [TRANSITION_AUTO_RECREATE_PAYMENT]: STATE_CREATED_PAYMENT,
        [TRANSITION_INITIATE_PAYMENT]: STATE_PENDING_PAYMENT,
        [TRANSITION_CANCEL_PRE_CREATE_PAYMENT]: STATE_CANCELLED,
        [TRANSITION_ADMIN_CREATE_PAYMENT]: STATE_CREATED_PAYMENT,
      },
    },
    [STATE_CREATED_PAYMENT]: {
      on: {
        [TRANSITION_HOLD_CAPTURE_PAYMENT]: STATE_PRE_AUTO_CAPTURE_PAYMENT,
        [TRANSITION_CANCEL_CREATED_PAYMENT]: STATE_CANCELLED,
      },
    },
    [STATE_PRE_AUTO_CAPTURE_PAYMENT]: {
      on: {
        [TRANSITION_AUTO_CAPTURE_PAYMENT]: STATE_CONFIRMED_PAYMENT,
        [TRANSITION_CANCEL_PRE_AUTO_CAPTURE_PAYMENT]: STATE_CANCELLED,
      },
    },
    [STATE_CONFIRMED_PAYMENT]: {
      on: {
        [TRANSITION_TRANSFERRED]: STATE_DELIVERED,
      },
    },
    [STATE_PENDING_PAYMENT]: {
      on: {
        [TRANSITION_CONFIRM_PAYMENT]: STATE_PRE_AUTO_CAPTURE_PAYMENT,
        [TRANSITION_EXPIRE_PAYMENT]: STATE_PAYMENT_EXPIRED,
      },
    },
    [STATE_DELIVERED]: {
      on: {
        [TRANSITION_DISPUTE]: STATE_DISPUTED,
        [TRANSITION_MARK_RETURN_ITEM]: STATE_RETURNED_ITEM,
        [TRANSITION_ADMIN_MARK_RETURN_ITEM]: STATE_RETURNED_ITEM,
        [TRANSITION_EXPIRE_RETURN_ITEM]: STATE_RETURNED_ITEM,
        [TRANSITION_REQUEST_EXTEND_BOOKING]: STATE_PRE_BOOKING_EXTEND,
        [TRANSITION_OFFER_EXTEND_BOOKING_DIRECTLY]: STATE_OFFERED_EXTEND_BOOKING,
      },
    },
    [STATE_RETURNED_ITEM]: {
      on: {
        [TRANSITION_START_REVIEW]: STATE_REVIEW_STARTING,
      },
    },
    [STATE_PRE_BOOKING_EXTEND]: {
      on: {
        [TRANSITION_MARK_RETURN_ITEM_AFTER_REQUEST_EXTEND_BOOKING]: STATE_RETURNED_ITEM,
        [TRANSITION_ADMIN_MARK_RETURN_ITEM_AFTER_REQUEST_EXTEND_BOOKING]: STATE_RETURNED_ITEM,
        [TRANSITION_EXPIRE_RETURN_ITEM_AFTER_REQUEST_EXTEND_BOOKING]: STATE_RETURNED_ITEM,
        [TRANSITION_OFFER_EXTEND_BOOKING]: STATE_OFFERED_EXTEND_BOOKING,
        [TRANSITION_DISPUTE_AFTER_REQUEST_EXTEND_BOOKING]: STATE_DISPUTED,
      },
    },
    [STATE_OFFERED_EXTEND_BOOKING]: {
      on: {
        [TRANSITION_DECLINE_EXTEND_BOOKING]: STATE_OFFERED_EXTEND_BOOKING,
        [TRANSITION_EXPIRE_RETURN_ITEM_AFTER_OFFERED_EXTEND_BOOKING]: STATE_RETURNED_ITEM,
        [TRANSITION_ACCEPT_EXTEND_BOOKING]: STATE_EXTENDED_BOOKING,
        [TRANSITION_MARK_RETURN_ITEM_AFTER_OFFER_EXTEND_BOOKING]: STATE_RETURNED_ITEM,
        [TRANSITION_ADMIN_MARK_RETURN_ITEM_AFTER_OFFER_EXTEND_BOOKING]: STATE_RETURNED_ITEM,
        [TRANSITION_SAVE_EXTEND_BOOKING]: STATE_OFFERED_EXTEND_BOOKING,
        [TRANSITION_DISPUTE_AFTER_OFFERED_EXTEND_BOOKING]: STATE_DISPUTED,
      },
    },
    [STATE_DISPUTED]: {
      on: {
        [TRANSITION_RESOLVED]: STATE_RETURNED_ITEM,
      },
    },
    [STATE_EXTENDED_BOOKING]: {
      on: {
        [TRANSITION_COMPLETE_BOOKING]: STATE_REVIEW_STARTING,
      },
    },
    [STATE_REVIEW_STARTING]: {
      on: {
        [TRANSITION_REVIEW_1_BY_PROVIDER]: STATE_REVIEWED_BY_PROVIDER,
        [TRANSITION_REVIEW_1_BY_CUSTOMER]: STATE_REVIEWED_BY_CUSTOMER,
        [TRANSITION_EXPIRE_REVIEW_PERIOD]: STATE_REVIEWED,
        [TRANSITION_REMIND_REVIEW]: STATE_REMINDED_REVIEW,
      },
    },
    [STATE_REVIEWED_BY_PROVIDER]: {
      on: {
        [TRANSITION_REVIEW_2_BY_CUSTOMER]: STATE_REVIEWED,
        [TRANSITION_EXPIRE_CUSTOMER_REVIEW_PERIOD]: STATE_REVIEWED,
        [TRANSITION_REMIND_CUSTOMER_REVIEW]: STATE_REMINDED_CUSTOMER_REVIEW,
      },
    },
    [STATE_REVIEWED_BY_CUSTOMER]: {
      on: {
        [TRANSITION_REVIEW_2_BY_PROVIDER]: STATE_REVIEWED,
        [TRANSITION_EXPIRE_PROVIDER_REVIEW_PERIOD]: STATE_REVIEWED,
        [TRANSITION_REMIND_PROVIDER_REVIEW]: STATE_REMINDED_PROVIDER_REVIEW,
      },
    },
    [STATE_REMINDED_REVIEW]: {
      on: {
        [TRANSITION_REVIEW_1_BY_PROVIDER_AFTER_REMINDER]: STATE_REVIEWED_BY_PROVIDER,
        [TRANSITION_REVIEW_1_BY_CUSTOMER_AFTER_REMINDER]: STATE_REVIEWED_BY_CUSTOMER,
        [TRANSITION_EXPIRE_REVIEW_PERIOD_AFTER_REMINDER]: STATE_REVIEWED,
      },
    },
    [STATE_REMINDED_CUSTOMER_REVIEW]: {
      on: {
        [TRANSITION_REVIEW_2_BY_CUSTOMER_AFTER_REMINDER]: STATE_REVIEWED,
        [TRANSITION_EXPIRE_CUSTOMER_REVIEW_PERIOD_AFTER_REMINDER]: STATE_REVIEWED,
      },
    },
    [STATE_REMINDED_PROVIDER_REVIEW]: {
      on: {
        [TRANSITION_REVIEW_2_BY_PROVIDER_AFTER_REMINDER]: STATE_REVIEWED,
        [TRANSITION_EXPIRE_PROVIDER_REVIEW_PERIOD_AFTER_REMINDER]: STATE_REVIEWED,
      },
    },
    [STATE_REVIEWED]: {
      type: 'final',
    },
  },
};

//Helper
export const isRelevantPastTransition = transition => {
  return [
    TRANSITION_REQUEST_PAYMENT,
    TRANSITION_REQUEST_PAYMENT_AFTER_ENQUIRY,
    TRANSITION_ACCEPT,
    TRANSITION_CANCEL,
    TRANSITION_COMPLETE,
    TRANSITION_CONFIRM_PAYMENT,
    TRANSITION_DECLINE,
    TRANSITION_EXPIRE,
    TRANSITION_CANCEL_BOOKING,
    TRANSITION_EXPIRE_PENDING_PAYMENT,
    TRANSITION_REQUEST_EXTEND_BOOKING,
    TRANSITION_DISPUTE,
    TRANSITION_DISPUTE_AFTER_OFFERED_EXTEND_BOOKING,
    TRANSITION_DISPUTE_AFTER_REQUEST_EXTEND_BOOKING,
    TRANSITION_MARK_RETURN_ITEM,
    TRANSITION_EXPIRE_RETURN_ITEM,
    TRANSITION_MARK_RETURN_ITEM_AFTER_REQUEST_EXTEND_BOOKING,
    TRANSITION_EXPIRE_RETURN_ITEM_AFTER_REQUEST_EXTEND_BOOKING,
    TRANSITION_MARK_RETURN_ITEM_AFTER_OFFER_EXTEND_BOOKING,
    TRANSITION_EXPIRE_RETURN_ITEM_AFTER_OFFERED_EXTEND_BOOKING,
    TRANSITION_START_REVIEW,
    TRANSITION_COMPLETE_BOOKING,
    TRANSITION_OFFER_EXTEND_BOOKING_DIRECTLY,
    TRANSITION_OFFER_EXTEND_BOOKING,
    TRANSITION_ACCEPT_EXTEND_BOOKING,
    TRANSITION_DECLINE_EXTEND_BOOKING,
    TRANSITION_TRANSFERRED,
    TRANSITION_REQUEST_OFFER,
    TRANSITION_RE_OFFER,
    TRANSITION_DECLINE_OFFER,
    TRANSITION_ACCEPT_NEGOTIATE_OFFER,
    TRANSITION_REVIEW_1_BY_CUSTOMER,
    TRANSITION_REVIEW_1_BY_PROVIDER,
    TRANSITION_REVIEW_2_BY_CUSTOMER,
    TRANSITION_REVIEW_2_BY_PROVIDER,
    TRANSITION_REVIEW_1_BY_PROVIDER_AFTER_REMINDER,
    TRANSITION_REVIEW_2_BY_PROVIDER_AFTER_REMINDER,
    TRANSITION_REVIEW_1_BY_CUSTOMER_AFTER_REMINDER,
    TRANSITION_REVIEW_2_BY_CUSTOMER_AFTER_REMINDER,
    TRANSITION_ADMIN_ACCEPT,
    TRANSITION_ADMIN_DECLINE,
    TRANSITION_ADMIN_MARK_RETURN_ITEM,
    TRANSITION_ADMIN_MARK_RETURN_ITEM_AFTER_REQUEST_EXTEND_BOOKING,
    TRANSITION_ADMIN_MARK_RETURN_ITEM_AFTER_OFFER_EXTEND_BOOKING,
    TRANSITION_RESCHEDULE,
    TRANSITION_ACCEPT_RESCHEDULE,
    TRANSITION_CANCEL_RESCHEDULE,
    TRANSITION_EXPIRE_RESCHEDULE,
    TRANSITION_CONFIRM_RESCHEDULE_ACCEPTANCE,
    TRANSITION_EXPIRE_RESCHEDULE_ACCEPTANCE,
  ].includes(transition);
};

export const getStateFromLastTransition = tx => {
  const lastTransition = txLastTransition(tx);
  const keys = Object.keys(stateDescription.states);
  const previousState = keys.find(key => stateDescription.states[key]?.on?.[lastTransition]);
  const currentState = previousState
    ? stateDescription.states[previousState]?.on?.[lastTransition]
    : null;
  const nextPosibleTransitions =
    currentState &&
      stateDescription.states[currentState] &&
      stateDescription.states[currentState].on
      ? Object.keys(stateDescription.states[currentState].on)
      : null;

  return nextPosibleTransitions;
};

// This is a list of all the transitions that this app should be able to handle.
export const TRANSITIONS = getTransitions(statesFromStateDescription(stateDescription)).map(
  t => t.key
);

// Get all the transitions that lead to specified state.
const getTransitionsToState = getTransitionsToStateFn(stateDescription);

export const hasPassedStateFn = state => tx =>
  getTransitionsToState(state).filter(t => hasPassedTransition(t, tx)).length > 0;

// This is needed to fetch transactions that need response from provider.
// I.e. transactions which provider needs to accept or decline
export const transitionsToRequested = getTransitionsToState(STATE_REQUESTED);

// For Inbox Filter - enquiryOnly
// all transitions when Provider and Customer are still dicussing a listing
export const transitionInboxFilterEnquiryOnly = [
  TRANSITION_ENQUIRE,
  ...getTransitionsToState(STATE_OFFER_REQUESTED),
  ...getTransitionsToState(STATE_REQUESTED),
];

// For Inbox Filter  - notYetCommenced
// all transition after STATE_ACCEPTED to STATE_PRE_AUTO_CAPTURE_PAYMENT
// CASE: mark-pending-payment happens 3 days before booking start => get transitions before STATE_ACCEPTED
export const transitionsInboxFilterNotYetCommence = [
  ...getTransitionsToState(STATE_ACCEPTED),
  ...getTransitionsToState(STATE_PRE_CREATE_PAYMENT),
  ...getTransitionsToState(STATE_CREATED_PAYMENT),
  ...getTransitionsToState(STATE_PRE_AUTO_CAPTURE_PAYMENT),
  ...getTransitionsToState(STATE_CONFIRMED_PAYMENT),
  ...getTransitionsToState(STATE_PENDING_PAYMENT),
];

// For Inbox Filter - currentRentals and endedRentals - require further filtering
// all transition from TRANSFERRED onward
export const transitionsInboxFilterCurrentOrEndedRentals = [
  TRANSITION_TRANSFERRED,
  TRANSITION_AUTO_CAPTURE_PAYMENT,
  ...getTransitionsToState(STATE_DISPUTED),
  ...getTransitionsToState(STATE_RETURNED_ITEM),
  ...getTransitionsToState(STATE_OFFERED_EXTEND_BOOKING),
  ...getTransitionsToState(STATE_EXTENDED_BOOKING),
  ...getTransitionsToState(STATE_REVIEW_STARTING),
  ...getTransitionsToState(STATE_REVIEWED_BY_CUSTOMER),
  ...getTransitionsToState(STATE_REVIEWED_BY_PROVIDER),
  ...getTransitionsToState(STATE_REMINDED_REVIEW),
  ...getTransitionsToState(STATE_REMINDED_CUSTOMER_REVIEW),
  ...getTransitionsToState(STATE_REMINDED_PROVIDER_REVIEW),
  ...getTransitionsToState(STATE_REVIEWED),
];

// For Inbox SORT - sort by item returned date = all transitions to STATE_RETURNED_ITEM
export const transitionsToReturnedItem = getTransitionsToState(STATE_RETURNED_ITEM);

export const txIsEnquired = tx =>
  getTransitionsToState(STATE_ENQUIRY).includes(txLastTransition(tx));

export const txIsNeedToReschedule = tx => {
  const lastTransition = txLastTransition(tx);
  return (
    lastTransition === TRANSITION_REQUEST_PAYMENT ||
    lastTransition === TRANSITION_REQUEST_PAYMENT_AFTER_ENQUIRY ||
    lastTransition === TRANSITION_ACCEPT_NEGOTIATE_OFFER
  );
};

export const txIsPaymentPending = tx => {
  const lastTransition = txLastTransition(tx);
  return (
    lastTransition === TRANSITION_MARK_PENDING_PAYMENT ||
    lastTransition === TRANSITION_INITIATE_PAYMENT
  );
};

export const txIsInitiatePayment = tx => txLastTransition(tx) === TRANSITION_INITIATE_PAYMENT;

export const txIsAutoRecreatedPayment = tx =>
  txLastTransition(tx) === TRANSITION_AUTO_RECREATE_PAYMENT;

export const txIsCreatedPayment = tx => {
  const lastTransition = txLastTransition(tx);
  return (
    lastTransition === TRANSITION_CREATE_PAYMENT ||
    lastTransition === TRANSITION_ADMIN_CREATE_PAYMENT
  );
};

export const txIsPaymentExpired = tx =>
  getTransitionsToState(STATE_PAYMENT_EXPIRED).includes(txLastTransition(tx));

// Note: state name used in Marketplace API docs (and here) is actually preauthorized
// However, word "requested" is used in many places so that we decided to keep it.
export const txIsRequested = tx =>
  getTransitionsToState(STATE_REQUESTED).includes(txLastTransition(tx));

export const txIsRescheduled = tx =>
  getTransitionsToState(STATE_RESCHEDULED).includes(txLastTransition(tx));

export const txIsRescheduleAccepted = tx =>
  getTransitionsToState(STATE_RESCHEDULE_ACCEPTED).includes(txLastTransition(tx));

export const txIsAccepted = tx =>
  getTransitionsToState(STATE_ACCEPTED).includes(txLastTransition(tx));

export const txIsDeclined = tx =>
  getTransitionsToState(STATE_DECLINED).includes(txLastTransition(tx));

export const txIsCanceled = tx =>
  getTransitionsToState(STATE_CANCELLED).includes(txLastTransition(tx));

export const txIsPaymentConfirmed = tx =>
  getTransitionsToState(STATE_PRE_AUTO_CAPTURE_PAYMENT).includes(txLastTransition(tx));

export const txIsDelivered = tx =>
  getTransitionsToState(STATE_DELIVERED).includes(txLastTransition(tx));

export const txConfirmedPayment = tx =>
  getTransitionsToState(STATE_CONFIRMED_PAYMENT).includes(txLastTransition(tx));

export const txRequestBookingExtend = tx =>
  txLastTransition(tx) === TRANSITION_REQUEST_EXTEND_BOOKING;

export const txIsOfferExtendBooking = tx => {
  const lastTransition = txLastTransition(tx);
  return (
    lastTransition === TRANSITION_OFFER_EXTEND_BOOKING ||
    lastTransition === TRANSITION_SAVE_EXTEND_BOOKING ||
    lastTransition === TRANSITION_OFFER_EXTEND_BOOKING_DIRECTLY
  );
};

export const txExtendedBooking = tx => txLastTransition(tx) === TRANSITION_ACCEPT_EXTEND_BOOKING;

export const txIsDeclineExtendBooking = tx =>
  txLastTransition(tx) === TRANSITION_DECLINE_EXTEND_BOOKING;

export const txCompleteBooking = tx => txLastTransition(tx) === TRANSITION_COMPLETE_BOOKING;

export const txIsTransferred = tx => txLastTransition(tx) === TRANSITION_TRANSFERRED;

export const txReview = hasPassedStateFn(STATE_REVIEW_STARTING);

export const txIsRemindedReview = tx => txLastTransition(tx) === TRANSITION_REMIND_REVIEW;

export const txIsRemindedCustomerReview = tx =>
  txLastTransition(tx) === TRANSITION_REMIND_CUSTOMER_REVIEW;

export const txIsRemindedProviderReview = tx =>
  txLastTransition(tx) === TRANSITION_REMIND_PROVIDER_REVIEW;

const firstReviewTransitions = [
  ...getTransitionsToState(STATE_REVIEWED_BY_CUSTOMER),
  ...getTransitionsToState(STATE_REVIEWED_BY_PROVIDER),
  ...getTransitionsToState(STATE_REMINDED_CUSTOMER_REVIEW),
  ...getTransitionsToState(STATE_REMINDED_PROVIDER_REVIEW),
];
export const txIsInFirstReview = tx => firstReviewTransitions.includes(txLastTransition(tx));

export const txIsInFirstReviewBy = (tx, isCustomer) =>
  isCustomer
    ? [
      ...getTransitionsToState(STATE_REVIEWED_BY_CUSTOMER),
      ...getTransitionsToState(STATE_REMINDED_PROVIDER_REVIEW),
    ].includes(txLastTransition(tx))
    : [
      ...getTransitionsToState(STATE_REVIEWED_BY_PROVIDER),
      ...getTransitionsToState(STATE_REMINDED_CUSTOMER_REVIEW),
    ].includes(txLastTransition(tx));

export const txIsReviewed = tx =>
  getTransitionsToState(STATE_REVIEWED).includes(txLastTransition(tx));

export const txIsStartingReview = tx =>
  getTransitionsToState(STATE_REVIEW_STARTING).includes(txLastTransition(tx)) ||
  getTransitionsToState(STATE_REMINDED_REVIEW).includes(txLastTransition(tx));

export const txIsReturnedItem = tx =>
  getTransitionsToState(STATE_RETURNED_ITEM).includes(txLastTransition(tx));

export const transitionIsReviewed = transition =>
  getTransitionsToState(STATE_REVIEWED).includes(transition);

export const transitionIsFirstReviewedBy = (transition, isCustomer) =>
  isCustomer
    ? getTransitionsToState(STATE_REVIEWED_BY_CUSTOMER).includes(transition)
    : getTransitionsToState(STATE_REVIEWED_BY_PROVIDER).includes(transition);

export const getReview1Transition = (isCustomer, tx) => {
  if (txIsRemindedReview(tx)) {
    return isCustomer
      ? TRANSITION_REVIEW_1_BY_CUSTOMER_AFTER_REMINDER
      : TRANSITION_REVIEW_1_BY_PROVIDER_AFTER_REMINDER;
  }
  return isCustomer ? TRANSITION_REVIEW_1_BY_CUSTOMER : TRANSITION_REVIEW_1_BY_PROVIDER;
}

export const getReview2Transition = (isCustomer, tx) => {
  if (txIsRemindedCustomerReview(tx) || txIsRemindedProviderReview(tx)) {
    return isCustomer
      ? TRANSITION_REVIEW_2_BY_CUSTOMER_AFTER_REMINDER
      : TRANSITION_REVIEW_2_BY_PROVIDER_AFTER_REMINDER;
  }
  return isCustomer ? TRANSITION_REVIEW_2_BY_CUSTOMER : TRANSITION_REVIEW_2_BY_PROVIDER;
}

export const isCustomerReview = transition => {
  return [
    TRANSITION_REVIEW_1_BY_CUSTOMER,
    TRANSITION_REVIEW_2_BY_CUSTOMER,
    TRANSITION_REVIEW_1_BY_CUSTOMER_AFTER_REMINDER,
    TRANSITION_REVIEW_2_BY_CUSTOMER_AFTER_REMINDER,
  ].includes(transition);
};

export const isProviderReview = transition => {
  return [
    TRANSITION_REVIEW_1_BY_PROVIDER,
    TRANSITION_REVIEW_2_BY_PROVIDER,
    TRANSITION_REVIEW_1_BY_PROVIDER_AFTER_REMINDER,
    TRANSITION_REVIEW_2_BY_PROVIDER_AFTER_REMINDER,
  ].includes(transition);
};

export const txHasBeenAccepted = hasPassedStateFn(STATE_ACCEPTED);
export const txHasBeenDelivered = hasPassedStateFn(STATE_DELIVERED);

//Negotiate
export const txIsOffer = tx =>
  getTransitionsToState(STATE_OFFER_REQUESTED).includes(txLastTransition(tx));

export const txIsOffered = tx =>
  [TRANSITION_REQUEST_OFFER, TRANSITION_RE_OFFER].includes(txLastTransition(tx));

export const txIsDeclineOffer = tx => txLastTransition(tx) === TRANSITION_DECLINE_OFFER;

export const txIsDisputed = tx =>
  getTransitionsToState(STATE_DISPUTED).includes(txLastTransition(tx));

// Check if the given transition is privileged.

// Privileged transitions need to be handled from a secure context,
// i.e. the backend. This helper is used to check if the transition
// should go through the local API endpoints, or if using JS SDK is
// enough.
export const isPrivileged = transition => {
  return [
    TRANSITION_REQUEST_PAYMENT,
    TRANSITION_REQUEST_PAYMENT_AFTER_ENQUIRY,
    TRANSITION_ACCEPT_NEGOTIATE_OFFER,
    TRANSITION_RESCHEDULE,
    TRANSITION_MARK_RETURN_ITEM,
    TRANSITION_MARK_RETURN_ITEM_AFTER_REQUEST_EXTEND_BOOKING,
    TRANSITION_MARK_RETURN_ITEM_AFTER_OFFER_EXTEND_BOOKING,
  ].includes(transition);
};

export const txPassedCapturePayment = hasPassedStateFn(STATE_PRE_AUTO_CAPTURE_PAYMENT);
export const txPassedCreatedPayment = hasPassedStateFn(STATE_CREATED_PAYMENT);

export const END_MAIN_TRANSACTION_TRANSITIONS = [
  ...getTransitionsToState(STATE_DECLINED),
  ...getTransitionsToState(STATE_PAYMENT_EXPIRED),
  ...getTransitionsToState(STATE_CANCELLED),
  ...getTransitionsToState(STATE_REVIEWED),
];

export const ONGOING_MAIN_TRANSACTION_TRANSITIONS = TRANSITIONS.filter(
  transition => !END_MAIN_TRANSACTION_TRANSITIONS.includes(transition)
);
